import { Box, Button, Card, Stack, Typography } from '@mui/material';
import { PlatformIcons } from './PlatformIcons';
import { pickPrimaryImage, pickTitle, type RenderableContentItem } from './ContentCard';
import { textOnImageBody, textOnImageHeading } from '../theme/text-on-image';

interface ReleaseCardProps {
  readonly item: RenderableContentItem;
  /** Optional primary affiliate purchase URL. When absent, no purchase button renders. */
  readonly purchaseUrl?: string;
  /**
   * Optional `/games/:gameId` URL for the "Learn more" button. When
   * absent, no Learn-more button renders. Resolved by the parent
   * (`<DailyPage>`) from the release's `gameId` encoded-body field
   * with a fallback to `item.id`.
   */
  readonly learnMoreUrl?: string;
}

/** Resolve the release subtitle from `summary`, `body`, or empty.
 * Falls back through the field-name variants the seed records and
 * the eventual smithy-generated types use.
 */
function pickSubtitle(item: RenderableContentItem): string {
  const candidate = (item as { summary?: unknown }).summary ?? item.body;
  return typeof candidate === 'string' ? candidate : '';
}

/**
 * Resolve the release-date badge shown on the top-right of the card.
 * Landed 2026-09-13 with the composer's release-window widening
 * (strict same-day → target+7 days) so viewers can distinguish a
 * "launches today" release from one launching later in the week.
 *
 * Compares the envelope's `releaseDate` against the record's own
 * daily-page `date` field:
 *   - `releaseDate === date` (same day) → green "TODAY" pill.
 *   - `releaseDate > date` → blue "Sep 14" pill (formatted short
 *     month/day). Falls back to a bare "Coming Soon" pill when the
 *     envelope has no `releaseDate`.
 *   - `releaseDate < date` (past — e.g., the daily-page renders an
 *     older day and the record's own release happened before that
 *     day) → no badge, just render as a historical release.
 */
function pickReleaseBadge(
  item: RenderableContentItem,
): { readonly label: string; readonly tone: 'today' | 'upcoming' } | undefined {
  const releaseDate = (item as { releaseDate?: unknown }).releaseDate;
  const dailyDate = (item as { date?: unknown }).date;
  if (typeof releaseDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(releaseDate)) {
    return undefined;
  }
  if (typeof dailyDate === 'string' && releaseDate === dailyDate) {
    return { label: 'Today', tone: 'today' };
  }
  if (typeof dailyDate === 'string' && releaseDate < dailyDate) return undefined;
  const parsed = new Date(`${releaseDate}T12:00:00Z`);
  if (!Number.isFinite(parsed.getTime())) return undefined;
  const label = new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  }).format(parsed);
  return { label, tone: 'upcoming' };
}

/**
 * `<ReleaseCard>` — image-background hero card used by the Daily page's
 * "Releases" grid per Requirement 3.1.6.
 *
 * Layout: 16:9 landscape aspect ratio (`aspect-ratio: 16 / 9`) so
 * every card scales uniformly inside the auto-fit grid the parent
 * lays out. The release `image` fills the card as a background; a
 * dark gradient overlay underneath the text keeps the foreground
 * legible across any cover artwork.
 *
 * Hover: a subtle scale-up + brightness lift on hover/focus mirrors
 * the in-grid `<AdSlot variant="release">` so the cards and ad
 * tiles share a uniform interactive feel.
 *
 * Content: title large at the top with a strong drop-shadow, subtitle
 * (line-clamped to 2 lines) just below, platform icons + Purchase +
 * Learn-more buttons anchored at the bottom. Platform icons match
 * the action button height so the bottom row reads as one
 * horizontal unit.
 */
export function ReleaseCard({ item, purchaseUrl, learnMoreUrl }: ReleaseCardProps): JSX.Element {
  const heading = pickTitle(item) || 'New release';
  const subtitle = pickSubtitle(item);
  const platforms = item.platforms ?? [];
  const image = pickPrimaryImage(item);
  const releaseBadge = pickReleaseBadge(item);
  return (
    <Card
      elevation={3}
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        overflow: 'hidden',
        borderRadius: 2,
        color: 'common.white',
        backgroundImage: image ? `url(${image})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: image ? 'transparent' : 'grey.900',
        transition: 'transform 200ms ease, box-shadow 200ms ease, filter 200ms ease',
        '&:hover, &:focus-within': {
          transform: 'translateY(-2px)',
          boxShadow: 8,
          filter: 'brightness(1.05)',
        },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          // Gradient overlay: dark band at the top 25% (anchors title
          // + subtitle), fully transparent middle 50% (lets the
          // artwork show), dark band at the bottom 25% (anchors the
          // platform icons + action buttons). Each dark band fades
          // smoothly from the edge so there's no hard line.
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 25%, rgba(0,0,0,0) 75%, rgba(0,0,0,0.65) 100%)',
          pointerEvents: 'none',
        }}
      />
      {releaseBadge !== undefined && (
        <Box
          sx={{
            position: 'absolute',
            top: 12, right: 12,
            zIndex: 2,
            px: 1.5, py: 0.5,
            borderRadius: 999,
            bgcolor: releaseBadge.tone === 'today' ? 'success.main' : 'primary.main',
            color: 'common.white',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            boxShadow: 2,
          }}
        >
          {releaseBadge.label}
        </Box>
      )}
      <Stack
        spacing={1}
        sx={{
          position: 'relative',
          zIndex: 1,
          height: '100%',
          padding: 2.5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Stack spacing={0.5}>
          <Typography
            variant="h3"
            component="h3"
            sx={{
              ...textOnImageHeading,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}
          >
            {heading}
          </Typography>
          {subtitle && (
            <Typography
              variant="body1"
              sx={{
                ...textOnImageBody,
                fontSize: { xs: '0.95rem', sm: '1rem' },
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            rowGap: 1,
          }}
        >
          {platforms.length > 0 ? (
            <PlatformIcons platforms={platforms} iconSize={36} />
          ) : (
            <Box />
          )}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
            {learnMoreUrl && (
              <Button
                href={learnMoreUrl}
                variant="outlined"
                color="inherit"
                size="medium"
                sx={{ minWidth: 0, height: 36 }}
              >
                Learn more
              </Button>
            )}
            {purchaseUrl && (
              <Button
                href={purchaseUrl}
                variant="contained"
                color="primary"
                size="medium"
                rel="sponsored noopener noreferrer"
                sx={{ minWidth: 0, height: 36 }}
              >
                Purchase
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}
